import { useMemo } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import { RateGauge, Tag, Tooltip } from "@impero-com/ui";
import { createColumnHelper } from "@tanstack/react-table";
import { Link } from "react-router-dom";

import { ProgramOverview } from "api/generated/model";

import * as tableTheme from "./ControlHealthStatusBreakdownTable.css";
import { CONTROL_HEALTH_PENDO } from "./pendo";
import { useBuildStatusBreakdownUrl } from "./useBuildStatusBreakdownUrl";
import * as theme from "./useProgramOverviewColumns.css";

const columnHelper = createColumnHelper<ProgramOverview>();
const statuses = [
    {
        key: "completed",
        pendoId: CONTROL_HEALTH_PENDO.chipCompleted,
        status: "Completed",
        label: "ControlHealth_Activities_Completed",
    },
    {
        key: "completedAfterDue",
        pendoId: CONTROL_HEALTH_PENDO.chipCompletedAfterDue,
        status: "CompletedAfterDueDate",
        label: "ControlResult_Status_CompletedAfterDue",
    },
    {
        key: "overdue",
        pendoId: CONTROL_HEALTH_PENDO.chipOverdue,
        status: "Overdue",
        label: "ControlHealth_Activities_Overdue",
    },
    {
        key: "pending",
        pendoId: CONTROL_HEALTH_PENDO.chipInProgress,
        status: "Pending",
        label: "ControlResult_Health_InProgress",
    },
    {
        key: "toBeSent",
        pendoId: undefined,
        // Scheduled activities do not have rows in the activity table yet.
        status: null,
        label: "ControlHealth_Activities_ToBeSent",
    },
] as const;

export function useProgramOverviewColumns({
    buildActivitiesUrl,
}: {
    buildActivitiesUrl: ReturnType<typeof useBuildStatusBreakdownUrl>;
}) {
    const { t } = useTranslation();
    const columns = useMemo(
        () => [
            columnHelper.accessor("completionRate", {
                header: t("ControlHealth_CompletionRate_Label"),
                size: 172,
                meta: {
                    fixedWidth: true,
                    headerPendoId: CONTROL_HEALTH_PENDO.sortCompletionRate,
                },
                cell: ({ getValue }) => (
                    <RateGauge size="xs" percentage={roundedRate(getValue())} />
                ),
            }),
            columnHelper.accessor("onTimeRate", {
                header: t("ControlHealth_OnTimeRate_Label"),
                size: 154,
                meta: {
                    fixedWidth: true,
                    headerPendoId: CONTROL_HEALTH_PENDO.sortOnTimeRate,
                },
                cell: ({ getValue }) => (
                    <RateGauge size="xs" percentage={roundedRate(getValue())} />
                ),
            }),
            columnHelper.accessor((program) => program.activities.total, {
                id: "activities",
                header: t("ControlHealth_ProgramOverview_Activities"),
                size: 362,
                meta: { headerPendoId: CONTROL_HEALTH_PENDO.sortActivities },
                cell: ({ row }) => (
                    <div className={tableTheme.chips}>
                        {statuses.map(({ key, status, label, pendoId }) => {
                            const count = row.original.activities[key];
                            const description = `${t(label)}: ${count}`;
                            const chip = (
                                <Tag
                                    variant="countPill"
                                    className={theme.chipTone[key]}
                                    icon={
                                        <span
                                            className={theme.statusDot[key]}
                                        />
                                    }
                                >
                                    {count}
                                </Tag>
                            );
                            return (
                                <Tooltip key={key} title={description}>
                                    {status === null ? (
                                        <span aria-label={description}>
                                            {chip}
                                        </span>
                                    ) : (
                                        <Link
                                            to={buildActivitiesUrl(
                                                row.original.controlProgramId,
                                                status
                                            )}
                                            aria-label={description}
                                            className={tableTheme.countLink}
                                            data-pendo={pendoId}
                                        >
                                            {chip}
                                        </Link>
                                    )}
                                </Tooltip>
                            );
                        })}
                    </div>
                ),
            }),
        ],
        [t, buildActivitiesUrl]
    );
    return {
        columns,
        controls: (
            <div className={theme.actions}>
                <div className={theme.legend}>
                    {statuses.map(({ key, label }) => (
                        <span key={key} className={theme.legendItem}>
                            <span className={theme.statusDot[key]} />
                            {t(label)}
                        </span>
                    ))}
                </div>
            </div>
        ),
    };
}

function roundedRate(rate: number | null | undefined): number | null {
    return rate === null || rate === undefined
        ? null
        : Math.round(rate * 10) / 10;
}
